import { CustomerInfo, CustomerListItem } from '../models/CustomerData';
import userService from './userService';
import logger from '../utils/logger';

const API_BASE_URL = process.env.REACT_APP_PULSE_API_BASE_URL || 'http://localhost:8080';

/**
 * Fetches the list of customers available to the current user.
 * In a real app, this would likely involve an authenticated API call.
 */
export const fetchUserCustomers = async (): Promise<CustomerListItem[]> => {

  logger.debug('Fetching user customers list from API...');
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    "X-API-KEY": process.env['REACT_APP_PULSE_API_KEY'] || ''
  };

  try {
    const userToken = await userService.getCurrentUserToken();

    // Optional but recommended: Check if userToken was successfully retrieved
    if (!userToken) {
      // If no token, we can't proceed. Throw an error to skip to the .catch block.
      logger.warn("Could not retrieve user token. Unable to fetch customers.");
      // Throw an error to be caught by the catch block, ensuring the promise rejects.
      throw new Error("User token is null or undefined; cannot fetch customers.");
    }
    
    headers['Authorization'] = `Bearer ${userToken}`;

    // get the customers list using authorization headers
    const response = await fetch(`${API_BASE_URL}/api/customers`, {
      method: 'GET',
      headers: headers,
    });

    // This block will execute *only after* fetch successfully completes
    if (!response.ok) {
      let errorDetailsMessage = response.statusText;
      try {
        // Attempt to parse a JSON error response from the server
        const errorData = await response.json();
        errorDetailsMessage = errorData.message || errorDetailsMessage;
      } catch (e) {
        // If parsing fails, stick with the status text.
        logger.warn('Failed to parse error response as JSON for fetching customers.', e);
      }
      throw new Error(`Failed to fetch customers: ${response.status} ${errorDetailsMessage}`);
    }
    
    const customersList = await response.json() as CustomerListItem[];
    // logger.debug("Customers List successfully fetched: ", customersList);
    return customersList;

  } catch (error: any) {
    logger.error('Error in fetchUserCustomers:', error);
    // Re-throw the error so the calling code can handle it and the promise correctly rejects.
    throw error; 
  }
};

/**
 * Fetches detailed information for a specific customer.
 */
export const fetchCustomerDetails = async (apiKey: string | null, customerId: string): Promise<CustomerInfo> => {

  logger.debug(`Fetching details for customer: ${customerId}`);
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    "X-API-KEY": apiKey || process.env['REACT_APP_PULSE_API_KEY'] || ''
  };

  try {
    const userToken = await userService.getCurrentUserToken();

    // Optional but recommended: Check if userToken was successfully retrieved
    if (!userToken) {
      // If no token, we can't proceed. Throw an error to skip to the .catch block.
      logger.warn("Could not retrieve user token. Unable to fetch customer details.");
      // Throw an error to be caught by the catch block, ensuring the promise rejects.
      throw new Error("User token is null or undefined; cannot fetch customer details.");
    }
    
    headers['Authorization'] = `Bearer ${userToken}`;

    // get the customers list using authorization headers
    const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}`, {
      method: 'GET',
      headers: headers,
    });

    // This block will execute *only after* fetch successfully completes
    if (!response.ok) {
      let errorDetailsMessage = response.statusText;
      try {
        // Attempt to parse a JSON error response from the server
        const errorData = await response.json();
        errorDetailsMessage = errorData.message || errorDetailsMessage;
      } catch (e) {
        // If parsing fails, stick with the status text.
        logger.warn('Failed to parse error response as JSON for fetching customer details.', e);
      }
      throw new Error(`Failed to fetch customer details: ${response.status} ${errorDetailsMessage}`);
    }
    
    const customrInfo = await response.json() as CustomerInfo;
    logger.info("CustomerInfo for selected customer: ", customrInfo );
    return customrInfo ;

  } catch (error: any) {
    logger.error(`Error fetching customer details for ${customerId}:`, error);
    // Re-throw the error so the calling code can handle it and the promise correctly rejects.
    throw error; 
  }
};